<script setup lang="ts">
import { h } from 'vue'
import { MISOProgram } from '@/composables/eos/program-earnings-dashboard'
import type { PRProgramEarningsHourEntry, ORProgramEarningsHourEntry } from '@/types/eos/ProgramEarningsDashboard'
import { currencyFormatter } from '@/helpers/ep/formatters'

defineProps<{
  programSelected: MISOProgram
  hours: PRProgramEarningsHourEntry[] | ORProgramEarningsHourEntry[]
}>()

function hourlyPerformanceFormatter(row: PRProgramEarningsHourEntry, _col: unknown, cellValue: unknown) {
  return h(
    'span',
    { class: row.hourly_performance_kwh < row.hourly_commitment_kwh ? 'text-orange-600' : 'text-emerald-500' },
    String(cellValue)
  )
}
</script>

<template>
  <!-- No data -->
  <div v-if="hours.length === 0" class="text-gray-500 p-6">No data for this day</div>

  <!-- PR (Price Response) table -->
  <el-table v-else-if="programSelected === MISOProgram.DAPR" :data="hours as PRProgramEarningsHourEntry[]">
    <el-table-column width="188px" />
    <el-table-column label="Hour Ending" prop="hour_ending" />
    <el-table-column label="Dispatch ID" prop="dispatch_id" />
    <el-table-column label="Hourly Commit. (KWh)" prop="hourly_commitment_kwh" />
    <el-table-column
      label="Hourly Perform. (KWh)"
      prop="hourly_performance_kwh"
      :formatter="hourlyPerformanceFormatter"
    />
    <el-table-column label="DA LMP" prop="da_lmp" align="right" :formatter="currencyFormatter" />
    <el-table-column label="RT LMP" prop="rt_lmp" align="right" :formatter="currencyFormatter" />
    <el-table-column label="Site Split (%)" prop="site_split_percent" />
    <el-table-column label="DA Earnings ($)" prop="da_earnings_usd" align="right" :formatter="currencyFormatter" />
    <el-table-column label="RT Earnings ($)" prop="rt_earnings_usd" align="right" :formatter="currencyFormatter" />
    <el-table-column
      label="Customer Earnings ($)"
      prop="customer_earnings_usd"
      align="right"
      :formatter="currencyFormatter"
    />
  </el-table>

  <!-- OR (Operating Reserves) table -->
  <el-table v-else :data="hours as ORProgramEarningsHourEntry[]">
    <el-table-column width="188px" />
    <el-table-column label="Hour Ending" prop="hour_ending" />
    <el-table-column label="Availability (KWh)" prop="availability_kwh" />
    <el-table-column label="RT Gen. Spin MCP" prop="rt_gen_spin_mcp" align="right" :formatter="currencyFormatter" />
    <el-table-column label="Performance Factor" prop="performance_factor" />
    <el-table-column label="Site Split (%)" prop="site_split_percent" />
    <el-table-column
      label="Customer Earnings ($)"
      prop="customer_earnings_usd"
      align="right"
      :formatter="currencyFormatter"
    />
  </el-table>
</template>

<style scoped lang="scss">
// Subtable header's style
:deep(.el-table__header th) {
  font-size: 0.75rem;
  background-color: var(--fill-table-stripe);
}

// These two clear the bottom border of the last row when the subtable is expanded, to avoid visual clutter with the parent table's expanded row border
:deep(.el-table__inner-wrapper::before) {
  display: none !important;
}

:deep(.el-table__row:last-child .el-table__cell) {
  border-bottom: none;
}
</style>
